import React from "react";
import { motion } from "framer-motion";
import { easing } from "../../lib/animations";
import { MonoLabel, SectionLabel } from "./primitives";

/**
 * PageShell — consistent dark canvas for every non-home page.
 * Composed of brutalist primitives (SectionLabel, MonoLabel) + display title.
 *
 * Vibe: brutalist, quiet luxury, "LESS NOISE".
 */
export const PageShell = ({
    sectionNumber,
    eyebrow,
    title,
    crumb,
    children,
    align = "left",
    "data-testid": testId,
}) => {
    const isCenter = align === "center";

    return (
        <section
            data-testid="page-shell"
            data-page={testId || "page-shell"}
            className="proof-page-shell"
            style={{
                position: "relative",
                background: "var(--ink)",
                color: "var(--on-ink)",
                minHeight: "100vh",
                padding:
                    "clamp(120px, 14vw, 200px) clamp(24px, 5vw, 80px) clamp(80px, 10vw, 160px)",
            }}
        >
            {/* Per-page sentinel — kept as a hidden marker so route-specific
                testids resolve while every PageShell instance also exposes a
                stable `page-shell` testid for shell-level assertions. */}
            {testId && testId !== "page-shell" && (
                <span
                    data-testid={testId}
                    style={{
                        position: "absolute",
                        width: 0,
                        height: 0,
                        overflow: "hidden",
                        opacity: 0,
                    }}
                    aria-hidden="true"
                />
            )}
            <SectionLabel
                sectionNumber={sectionNumber}
                crumb={crumb || title}
                data-testid="page-shell-meta"
            />

            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "clamp(14px, 1.4vw, 22px)",
                    marginBottom: "clamp(40px, 5vw, 76px)",
                    textAlign: isCenter ? "center" : "left",
                    alignItems: isCenter ? "center" : "flex-start",
                }}
            >
                {eyebrow && (
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                            duration: 0.7,
                            delay: 0.1,
                            ease: easing.primary,
                        }}
                        data-testid="page-shell-eyebrow"
                    >
                        <MonoLabel tone="mute">{eyebrow}</MonoLabel>
                    </motion.div>
                )}
                <motion.h1
                    initial={{ opacity: 0, y: 28 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                        duration: 0.95,
                        delay: 0.18,
                        ease: easing.primary,
                    }}
                    data-testid="page-shell-title"
                    style={{
                        margin: 0,
                        fontFamily: "var(--font-display)",
                        fontWeight: 700,
                        fontSize: "clamp(48px, 8vw, 144px)",
                        letterSpacing: "-0.045em",
                        lineHeight: 0.9,
                        textTransform: "uppercase",
                        color: "var(--on-ink)",
                    }}
                >
                    {title}
                </motion.h1>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                    duration: 0.9,
                    delay: 0.28,
                    ease: easing.primary,
                }}
            >
                {children}
            </motion.div>
        </section>
    );
};

export default PageShell;
